import { MAX_SAFE_FINANCIAL_UNITS } from '../money.js';
export const PPM_SCALE = 1000000n;
export const JERSEY_STANDARD_GST_PPM = 50_000;
function assertInteger(value, name, minimum, maximum = Number(MAX_SAFE_FINANCIAL_UNITS)) {
    if (!Number.isSafeInteger(value) || value < minimum || value > maximum)
        throw new RangeError(`${name} is outside the supported exact integer range`);
}
/** Round a non-negative rational to the nearest integer; exact halves round upward. */
export function divideRoundHalfUp(numerator, denominator) {
    if (numerator < 0n || denominator <= 0n)
        throw new RangeError('Rounding accepts a non-negative numerator and positive denominator');
    const quotient = numerator / denominator;
    return numerator % denominator * 2n >= denominator ? quotient + 1n : quotient;
}
function numberWithinBounds(value, name) {
    if (value < 0n || value > MAX_SAFE_FINANCIAL_UNITS)
        throw new RangeError(`${name} exceeds the supported financial bound`);
    return Number(value);
}
/** Inclusive-GST split, rounded once per final line after all discounts. Gross is always preserved exactly. */
export function splitInclusiveTax(grossPence, taxRatePpm) {
    assertInteger(grossPence, 'Gross', 0);
    assertInteger(taxRatePpm, 'Tax rate', 0, 1_000_000);
    const gross = BigInt(grossPence);
    const net = divideRoundHalfUp(gross * PPM_SCALE, PPM_SCALE + BigInt(taxRatePpm));
    return { netPence: numberWithinBounds(net, 'Net'), gstPence: numberWithinBounds(gross - net, 'GST') };
}
/** Largest-remainder allocation. Equal remainders are resolved by original line sequence. */
export function allocateProportionally(totalPence, weightsPence) {
    assertInteger(totalPence, 'Allocation total', 0);
    const weights = weightsPence.map((value) => { assertInteger(value, 'Allocation weight', 0); return BigInt(value); });
    const totalWeight = weights.reduce((sum, value) => sum + value, 0n);
    if (totalPence === 0)
        return weights.map(() => 0);
    if (totalWeight === 0n || BigInt(totalPence) > totalWeight)
        throw new RangeError('Allocation total cannot exceed its positive weights');
    const weighted = weights.map((weight, index) => ({ index,
        floor: BigInt(totalPence) * weight / totalWeight, remainder: BigInt(totalPence) * weight % totalWeight }));
    let residue = BigInt(totalPence) - weighted.reduce((sum, item) => sum + item.floor, 0n);
    const order = [...weighted].sort((left, right) => left.remainder === right.remainder ? left.index - right.index : left.remainder > right.remainder ? -1 : 1);
    const result = weighted.map((item) => item.floor);
    for (const item of order) {
        if (residue === 0n)
            break;
        result[item.index] = (result[item.index] ?? 0n) + 1n;
        residue -= 1n;
    }
    return result.map((value) => numberWithinBounds(value, 'Allocation'));
}
/** Cumulative linked-refund allocation. Dependent values are derived so every partial refund reconciles. */
export function calculateCumulativeRefundComponents(original, soldQuantity, cumulativeRefundQuantity, allocation = 'LEGACY') {
    assertInteger(soldQuantity, 'Sold quantity', 1, 1_000_000);
    assertInteger(cumulativeRefundQuantity, 'Cumulative refund quantity', 1, soldQuantity);
    const componentValues = [
        ['Line gross', original.lineGrossBeforeDiscountPence], ['Item discount', original.itemDiscountPence],
        ['Basket discount', original.basketDiscountPence], ['Customer discount', original.customerDiscountPence], ['Net', original.netPence], ['GST', original.gstPence], ['Gross', original.grossPence],
    ];
    for (const [name, value] of componentValues)
        assertInteger(value, name, 0);
    const totalDiscount = original.itemDiscountPence + original.basketDiscountPence + original.customerDiscountPence;
    if (original.lineGrossBeforeDiscountPence - totalDiscount !== original.grossPence || original.netPence + original.gstPence !== original.grossPence) {
        throw new RangeError('Original refund components do not reconcile');
    }
    const cumulative = BigInt(cumulativeRefundQuantity);
    const sold = BigInt(soldQuantity);
    const lineGrossBeforeDiscountPence = numberWithinBounds(divideRoundHalfUp(BigInt(original.lineGrossBeforeDiscountPence) * cumulative, sold), 'Refund line gross');
    const cumulativeTotalDiscount = numberWithinBounds(divideRoundHalfUp(BigInt(totalDiscount) * cumulative, sold), 'Refund discount');
    const itemDiscountPence = totalDiscount === 0 ? 0 : numberWithinBounds(divideRoundHalfUp(BigInt(original.itemDiscountPence) * BigInt(cumulativeTotalDiscount), BigInt(totalDiscount)), 'Refund item discount');
    // Monetary-mode sales allocate the residual after item discount so successive
    // partial refunds cannot invent customer discount or decrease a component.
    // Historical sales retain their original allocation convention.
    const remainingDiscount = cumulativeTotalDiscount - itemDiscountPence;
    const basketAndCustomer = original.basketDiscountPence + original.customerDiscountPence;
    const basketDiscountPence = allocation === 'MONETARY_V1'
        ? basketAndCustomer === 0 ? 0 : numberWithinBounds(divideRoundHalfUp(BigInt(original.basketDiscountPence) * BigInt(remainingDiscount), BigInt(basketAndCustomer)), 'Refund basket discount')
        : totalDiscount === 0 ? 0 : numberWithinBounds(divideRoundHalfUp(BigInt(original.basketDiscountPence) * BigInt(cumulativeTotalDiscount), BigInt(totalDiscount)), 'Refund basket discount');
    const customerDiscountPence = cumulativeTotalDiscount - itemDiscountPence - basketDiscountPence;
    const grossPence = lineGrossBeforeDiscountPence - cumulativeTotalDiscount;
    if (grossPence < 0)
        throw new RangeError('Refund discount exceeds its line gross');
    const netPence = original.grossPence === 0 || grossPence === 0 ? 0 : numberWithinBounds(divideRoundHalfUp(BigInt(original.netPence) * BigInt(grossPence), BigInt(original.grossPence)), 'Refund net');
    const gstPence = grossPence - netPence;
    return { lineGrossBeforeDiscountPence, itemDiscountPence, basketDiscountPence, customerDiscountPence, netPence, gstPence, grossPence };
}
export function calculateSale(lines, basketDiscountRatePpm, customer, basketDiscountAmountPence) {
    if (lines.length < 1 || lines.length > 100)
        throw new RangeError('A sale requires between 1 and 100 lines');
    assertInteger(basketDiscountRatePpm, 'Basket discount rate', 0, 1_000_000);
    const prepared = lines.map((line, index) => {
        assertInteger(line.quantity, 'Quantity', 1, 1_000_000);
        assertInteger(line.unitPriceIncGstPence, 'Unit price', 0);
        assertInteger(line.itemDiscountRatePpm, 'Item discount rate', 0, 1_000_000);
        assertInteger(line.taxRatePpm, 'Tax rate', 0, 1_000_000);
        assertInteger(line.standardCostExGstUnits, 'Standard cost', 0);
        if (line.discountsDisabled && (line.itemDiscountRatePpm > 0 || (line.itemDiscountAmountPence ?? 0) > 0 ||
            (line.setUnitPriceIncGstPence !== undefined && line.setUnitPriceIncGstPence < line.unitPriceIncGstPence))) {
            throw new RangeError('Discounts are disabled for this product category.');
        }
        const before = BigInt(line.unitPriceIncGstPence) * BigInt(line.quantity);
        if ([line.itemDiscountRatePpm > 0, line.itemDiscountAmountPence !== undefined, line.setUnitPriceIncGstPence !== undefined].filter(Boolean).length > 1)
            throw new RangeError('Choose one item discount mode');
        let itemDiscount = divideRoundHalfUp(before * BigInt(line.itemDiscountRatePpm), PPM_SCALE);
        if (line.itemDiscountAmountPence !== undefined) {
            assertInteger(line.itemDiscountAmountPence, 'Item amount off', 0);
            itemDiscount = BigInt(line.itemDiscountAmountPence);
        }
        if (line.setUnitPriceIncGstPence !== undefined) {
            assertInteger(line.setUnitPriceIncGstPence, 'New unit price', 0, line.unitPriceIncGstPence);
            itemDiscount = before - BigInt(line.setUnitPriceIncGstPence) * BigInt(line.quantity);
        }
        if (itemDiscount > before)
            throw new RangeError('Item discount cannot exceed the line value');
        return { ...line, sequence: index + 1, lineGrossBeforeDiscountPence: numberWithinBounds(before, 'Line gross'),
            itemDiscountPence: numberWithinBounds(itemDiscount, 'Item discount'), afterItemPence: numberWithinBounds(before - itemDiscount, 'Line after item discount') };
    });
    if (customer)
        assertInteger(customer.discountRatePpm, 'Customer discount rate', 0, 1_000_000);
    // This historical rate included GST relief; it is never a post-exemption commercial rate.
    if (customer?.behavior === 'GST_EXEMPT' && customer.discountRatePpm === 71_428)
        throw new RangeError('Legacy GST-exempt rate requires configuration migration before pricing');
    // Keep original sequence for largest-remainder ties; protected lines carry zero weight.
    const eligibleWeights = prepared.map(line => line.discountsDisabled ? 0 : line.afterItemPence);
    const afterItemTotal = eligibleWeights.reduce((sum, weight) => sum + BigInt(weight), 0n);
    if (prepared.every(line => line.discountsDisabled) && (basketDiscountRatePpm > 0 || (basketDiscountAmountPence ?? 0) > 0)) {
        throw new RangeError('There are no discount-eligible items in the basket.');
    }
    if (basketDiscountAmountPence !== undefined) {
        assertInteger(basketDiscountAmountPence, 'Basket amount off', 0);
        if (basketDiscountRatePpm !== 0)
            throw new RangeError('Choose one basket discount mode');
        if (BigInt(basketDiscountAmountPence) > afterItemTotal)
            throw new RangeError('Basket discount cannot exceed the remaining discount-eligible basket');
    }
    const customerWins = customer && (basketDiscountAmountPence === undefined
        ? customer.discountRatePpm >= basketDiscountRatePpm
        : BigInt(customer.discountRatePpm) * afterItemTotal >= BigInt(basketDiscountAmountPence) * PPM_SCALE);
    const standardCustomerWins = customer?.behavior === 'STANDARD_DISCOUNT' && customerWins;
    const exemptCustomerWins = customer?.behavior === 'GST_EXEMPT' && customerWins;
    const effectiveBasketRate = customer?.behavior === 'INTERNAL_BUILDS' || exemptCustomerWins ? 0 : standardCustomerWins ? customer.discountRatePpm : basketDiscountRatePpm;
    const effectiveDiscount = basketDiscountAmountPence !== undefined && !standardCustomerWins && !exemptCustomerWins && customer?.behavior !== 'INTERNAL_BUILDS'
        ? BigInt(basketDiscountAmountPence) : divideRoundHalfUp(afterItemTotal * BigInt(effectiveBasketRate), PPM_SCALE);
    const effectiveAllocations = allocateProportionally(numberWithinBounds(effectiveDiscount, 'Effective basket/customer discount'), eligibleWeights);
    const calculatedLines = prepared.map(({ afterItemPence, ...line }, index) => {
        const allocated = effectiveAllocations[index] ?? 0;
        const basketDiscountPence = standardCustomerWins ? 0 : allocated;
        let customerDiscountPence = standardCustomerWins ? allocated : 0;
        let grossPence = afterItemPence - allocated;
        let appliedTaxRatePpm = line.taxRatePpm;
        if (customer?.behavior === 'GST_EXEMPT') {
            const withoutTax = splitInclusiveTax(grossPence, line.taxRatePpm).netPence;
            customerDiscountPence += grossPence - withoutTax;
            grossPence = withoutTax;
            appliedTaxRatePpm = 0;
            if (exemptCustomerWins && !line.discountsDisabled) {
                const commercialDiscount = numberWithinBounds(divideRoundHalfUp(BigInt(withoutTax) * BigInt(customer.discountRatePpm), PPM_SCALE), 'GST-exempt commercial discount');
                customerDiscountPence += commercialDiscount;
                grossPence -= commercialDiscount;
            }
        }
        else if (customer?.behavior === 'INTERNAL_BUILDS') {
            customerDiscountPence = afterItemPence;
            grossPence = 0;
            appliedTaxRatePpm = 0;
        }
        const tax = splitInclusiveTax(grossPence, appliedTaxRatePpm);
        return { ...line, configuredTaxRatePpm: line.taxRatePpm, taxRatePpm: appliedTaxRatePpm, basketDiscountPence, customerDiscountPence,
            totalDiscountPence: line.itemDiscountPence + basketDiscountPence + customerDiscountPence, grossPence, netPence: tax.netPence, gstPence: tax.gstPence };
    });
    const sum = (selector) => numberWithinBounds(calculatedLines.reduce((total, line) => total + BigInt(selector(line)), 0n), 'Sale total');
    const subtotalBeforeDiscountsPence = sum((line) => line.lineGrossBeforeDiscountPence);
    const itemDiscountTotalPence = sum((line) => line.itemDiscountPence);
    const basketDiscountTotalPence = sum((line) => line.basketDiscountPence);
    const customerDiscountTotalPence = sum(line => line.customerDiscountPence);
    const netTotalPence = sum((line) => line.netPence);
    const gstTotalPence = sum((line) => line.gstPence);
    const grossTotalPence = sum((line) => line.grossPence);
    return { lines: calculatedLines, subtotalBeforeDiscountsPence, itemDiscountTotalPence, basketDiscountTotalPence, customerDiscountTotalPence,
        totalDiscountPence: itemDiscountTotalPence + basketDiscountTotalPence + customerDiscountTotalPence, netTotalPence, gstTotalPence,
        grossTotalPence, basketDiscountRatePpm };
}
